import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { cn } from '@/lib/utils.ts'

export const TooltipProvider = TooltipPrimitive.Provider

const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, collisionPadding = 8, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    collisionPadding={collisionPadding}
    className={cn(
      'z-popover max-w-xs rounded-md bg-gray-900 px-2 py-1 text-caption text-white shadow-popover',
      'animate-fade-in',
      className,
    )}
    {...props}
  />
))
TooltipContent.displayName = 'TooltipContent'

interface TooltipProps {
  content: ReactNode
  children: ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  delayDuration?: number
  disabled?: boolean
}

/**
 * Einfacher Tooltip-Wrapper fuer den haeufigsten Fall.
 * Fuer komplexere Nutzung (verzoegerter Open-Zustand, kontrolliert) direkt
 * die Radix-Primitives verwenden.
 *
 * Erfordert <TooltipProvider> im Root der App.
 */
export function Tooltip({ content, children, side = 'top', delayDuration = 300, disabled }: TooltipProps) {
  if (disabled) return <>{children}</>
  return (
    <TooltipPrimitive.Root delayDuration={delayDuration}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipContent side={side}>{content}</TooltipContent>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  )
}
